import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarCheck, ListTree, ShieldCheck, Scale } from "lucide-react";
import { Txt, ItemTools, AddBtn } from "@/components/public/Editable";

const SECTION_RX = /^SECTION\s*:\s*(\d+)\s*:\s*(.+)$/i;
const SUBSECTION_RX = /^SUBSECTION\s*:\s*(.+)$/i;
const TABLE_START_RX = /^TABLE_START\s*:\s*(.+)$/i;
const TABLE_END_RX = /^TABLE_END\s*$/i;
const EFFECTIVE_RX = /^EFFECTIVE_DATE\s*:\s*(.+)$/i;

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function parseBlocks(paragraphs) {
  const blocks = [];
  let table = null;
  for (const raw of paragraphs) {
    const p = String(raw || "").trim();
    let m;
    if ((m = p.match(EFFECTIVE_RX))) {
      blocks.push({ type: "effective", text: m[1].trim() });
    } else if ((m = p.match(SECTION_RX))) {
      blocks.push({ type: "section", num: m[1].trim(), title: m[2].trim(), id: `sec-${m[1].trim()}-${slugify(m[2].trim())}` });
    } else if ((m = p.match(SUBSECTION_RX))) {
      blocks.push({ type: "subsection", title: m[1].trim() });
    } else if ((m = p.match(TABLE_START_RX))) {
      table = { type: "table", caption: m[1].trim(), headers: [], rows: [] };
      blocks.push(table);
    } else if (TABLE_END_RX.test(p)) {
      table = null;
    } else if (table && p.startsWith("|")) {
      const cells = p.split("|").slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^:?-+:?$/.test(c))) continue;
      if (!table.headers.length) table.headers = cells;
      else table.rows.push(cells);
    } else {
      blocks.push({ type: "para", html: p });
      table = null;
    }
  }
  return blocks;
}

function useScrollSpy(ids) {
  const [active, setActive] = useState(ids[0] || "");
  const key = ids.join(",");
  useEffect(() => {
    const list = key.split(",").filter(Boolean);
    if (!list.length) return;
    let ticking = false;
    const update = () => {
      let current = list[0];
      for (const id of list) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 160) current = id;
      }
      setActive(current);
      ticking = false;
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, [key]);
  return active;
}

function Toc({ sections, pageKey, active }) {
  if (sections.length < 3) return null;
  return (
    <nav className="hidden lg:block sticky top-28 self-start w-72 shrink-0" data-testid={`${pageKey}-toc`} data-spy-version="v2-scroll" aria-label="On this page">
      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/[0.03] p-5">
        <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#17263B]/60">
          <span className="w-6 h-6 rounded-lg bg-[#FFC72C]/15 flex items-center justify-center"><ListTree className="w-3.5 h-3.5 text-[#9A7B0A]" /></span>
          On this page
        </p>
        <ul className="mt-4 flex flex-col max-h-[60vh] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
          {sections.map((s) => {
            const isActive = active === s.id;
            return (
              <li key={s.id}>
                <a href={`#${s.id}`}
                  className={`group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-semibold leading-snug transition-colors ${
                    isActive ? "bg-[#FFC72C]/15 text-[#17263B]" : "text-slate-500 hover:bg-slate-50 hover:text-[#17263B]"
                  }`}
                  data-testid={`${pageKey}-toc-${s.id}`}>
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-extrabold shrink-0 transition-colors ${
                    isActive ? "bg-[#FFC72C] text-[#17263B]" : "bg-slate-100 text-slate-400 group-hover:bg-slate-200"
                  }`}>{s.num}</span>
                  {s.title}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

function MobileToc({ sections, pageKey }) {
  if (sections.length < 3) return null;
  return (
    <div className="lg:hidden sticky top-[76px] z-30 -mx-4 px-4 py-3 bg-white/90 backdrop-blur-md border-b border-slate-100 mb-8" data-testid={`${pageKey}-toc-mobile`}>
      <div className="flex gap-2 overflow-x-auto whitespace-nowrap" style={{ scrollbarWidth: "none" }}>
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`}
            className="shrink-0 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:border-[#FFC72C] hover:text-[#17263B] transition-colors"
            data-testid={`${pageKey}-tocm-${s.id}`}>
            {s.num}. {s.title}
          </a>
        ))}
      </div>
    </div>
  );
}

function RichBlock({ block, i, pageKey }) {
  if (block.type === "effective") {
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-[#17263B] px-4 py-2 text-xs font-bold text-white mb-10 shadow-sm" data-testid={`${pageKey}-effective-date`}>
        <CalendarCheck className="w-4 h-4 text-[#FFC72C]" /> Effective date: {block.text}
      </div>
    );
  }
  if (block.type === "section") {
    return (
      <div id={block.id} className="scroll-mt-28 mt-14 mb-6 first:mt-0 group" data-testid={`${pageKey}-section-${block.num}`}>
        <div className="flex items-center gap-4">
          <span className="relative w-11 h-11 rounded-2xl bg-[#17263B] text-[#FFC72C] flex items-center justify-center text-base font-extrabold shrink-0 shadow-md shadow-[#17263B]/20">
            {block.num}
            <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-md bg-[#FFC72C] border-2 border-white" aria-hidden="true" />
          </span>
          <h2 className="text-2xl sm:text-[1.75rem] font-extrabold tracking-tight text-[#17263B] leading-tight">{block.title}</h2>
        </div>
        <div className="mt-4 h-px bg-gradient-to-r from-[#FFC72C] via-slate-200 to-transparent" aria-hidden="true" />
      </div>
    );
  }
  if (block.type === "subsection") {
    return (
      <h3 className="flex items-center gap-2.5 text-base sm:text-lg font-extrabold text-[#17263B] mt-8 mb-3">
        <span className="w-1.5 h-5 rounded-full bg-[#FFC72C] shrink-0" aria-hidden="true" />
        {block.title}
      </h3>
    );
  }
  if (block.type === "table") {
    return (
      <div className="my-8 overflow-hidden rounded-2xl border border-slate-200 shadow-sm shadow-slate-900/[0.03]" data-testid={`${pageKey}-table-${i}`}>
        {block.caption && (
          <p className="px-6 py-4 bg-[#17263B] text-xs font-extrabold uppercase tracking-[0.15em] text-white/90">{block.caption}</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left border-b border-slate-200">
                {block.headers.map((h, j) => <th key={j} className="px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-slate-500">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((r, j) => (
                <tr key={j} className={`border-t border-slate-100 ${j % 2 ? "bg-slate-50/60" : "bg-white"} hover:bg-[#FFC72C]/[0.06] transition-colors`}>
                  {r.map((c, k) => <td key={k} className={`px-6 py-4 align-top leading-relaxed ${k === 0 ? "font-bold text-[#17263B]" : "text-slate-600"}`}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div className="legal-para text-[15px] sm:text-base text-slate-600 leading-[1.85] mb-5 [&_ul]:my-4 [&_ul]:space-y-2.5 [&_ul]:pl-1 [&_ul_li]:relative [&_ul_li]:pl-6 [&_ul_li]:leading-relaxed [&_ul_li]:before:content-[''] [&_ul_li]:before:absolute [&_ul_li]:before:left-0 [&_ul_li]:before:top-[0.62em] [&_ul_li]:before:w-2 [&_ul_li]:before:h-2 [&_ul_li]:before:rounded-[3px] [&_ul_li]:before:bg-[#FFC72C] [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4 [&_ol]:space-y-2.5 [&_ol]:marker:font-bold [&_ol]:marker:text-[#9A7B0A] [&_b]:text-[#17263B] [&_b]:font-bold [&_strong]:text-[#17263B] [&_strong]:font-bold"
      dangerouslySetInnerHTML={{ __html: block.html }} data-testid={`${pageKey}-para-${i}`} />
  );
}

export default function LegalDocSections({ data, edit, pageKey = "privacy" }) {
  const paragraphs = data?.paragraphs || [];
  const blocks = useMemo(() => parseBlocks(paragraphs), [paragraphs]);
  const sections = blocks.filter((b) => b.type === "section");
  const hasStructured = sections.length > 0;
  const active = useScrollSpy(sections.map((s) => s.id));
  const effective = blocks.find((b) => b.type === "effective");
  const bodyBlocks = blocks.filter((b) => b.type !== "effective");
  const titleText = data?.title || "";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid={`legal-page-${pageKey}`}>
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(120deg, #0A0F2B 0%, #17263B 55%, #14263C 100%)" }}>
        <div className="absolute inset-0 opacity-[0.35] pointer-events-none" aria-hidden="true"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.09) 1px, transparent 0)", backgroundSize: "26px 26px" }} />
        <div className="absolute -right-32 -top-32 w-[28rem] h-[28rem] rounded-full bg-[#FFC72C]/15 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -left-20 -bottom-24 w-72 h-72 rounded-full bg-[#FFC72C]/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
          {!edit && (
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/50 hover:text-white transition-colors" data-testid="legal-back">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          )}
          <div className="flex items-start gap-5 mt-10">
            <span className="hidden sm:flex w-14 h-14 rounded-2xl bg-[#FFC72C] items-center justify-center shadow-lg shadow-[#FFC72C]/25 shrink-0">
              <Scale className="w-7 h-7 text-[#17263B]" />
            </span>
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FFC72C]" /> Legal
              </span>
              <Txt edit={edit} path="title" value={titleText} as="h1" placeholder="Page title"
                className="block text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mt-4 leading-[1.05]" />
              {effective && !edit && (
                <p className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-white/60" data-testid={`${pageKey}-effective-hero`}>
                  <CalendarCheck className="w-4 h-4 text-[#FFC72C]" /> Effective date: {effective.text}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="h-6 bg-gradient-to-b from-transparent to-black/10" aria-hidden="true" />
      </section>

      {edit ? (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-12" data-testid="legal-body">
          {hasStructured && (
            <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50/70 px-5 py-4" data-testid={`${pageKey}-structure-hint`}>
              <p className="text-xs font-extrabold text-amber-800 uppercase tracking-wider">Structured page</p>
              <p className="text-xs text-amber-900/80 mt-1.5 leading-relaxed">
                This page renders with the designed layout (table of contents, numbered sections, tables) on the live site.
                The markers below — <code className="font-mono bg-white/70 px-1 rounded">SECTION: n: Title</code>, <code className="font-mono bg-white/70 px-1 rounded">SUBSECTION: Title</code>, <code className="font-mono bg-white/70 px-1 rounded">TABLE_START / TABLE_END</code>, <code className="font-mono bg-white/70 px-1 rounded">EFFECTIVE_DATE: …</code> — control that layout.
                Edit text freely, but keep the marker lines intact. Use <b>Save</b>, then view the live page to see the designed result.
              </p>
            </div>
          )}
          {paragraphs.map((p, i) => (
            <div key={i} className="relative group" data-testid={`${pageKey}-para-wrap-${i}`}>
              <Txt edit={edit} path={`paragraphs.${i}`} value={p} as="p" placeholder="Write paragraph…"
                className="block text-sm sm:text-base text-slate-600 leading-relaxed mb-5" />
              <ItemTools edit={edit} path="paragraphs" index={i} count={paragraphs.length} />
            </div>
          ))}
          <AddBtn edit={edit} path="paragraphs" blank="New paragraph…" label="Add paragraph" />
        </section>
      ) : hasStructured ? (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="legal-body">
          <MobileToc sections={sections} pageKey={pageKey} />
          <div className="flex gap-10">
            <Toc sections={sections} pageKey={pageKey} active={active} />
            <div className="min-w-0 flex-1 max-w-3xl">
              {bodyBlocks.map((b, i) => <RichBlock key={i} block={b} i={i} pageKey={pageKey} />)}
              <div className="mt-16 rounded-2xl border border-slate-200 bg-white px-6 py-5 flex items-center gap-4 shadow-sm" data-testid={`${pageKey}-footer-note`}>
                <span className="w-10 h-10 rounded-xl bg-[#FFC72C]/15 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#9A7B0A]" />
                </span>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Questions about this page? Write to us — we respond to every privacy or legal request.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12" data-testid="legal-body">
          {paragraphs.map((p, i) => (
            <div key={i} className="legal-para text-sm sm:text-base text-slate-600 leading-[1.85] mb-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul]:space-y-2 [&_b]:text-[#17263B] [&_strong]:text-[#17263B]"
              dangerouslySetInnerHTML={{ __html: p }} data-testid={`${pageKey}-para-${i}`} />
          ))}
        </section>
      )}
    </div>
  );
}
